/** @module @liquidefy/code-editor/git/git-branch @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  EditorResult,
  GitRepositoryState,
  GitBranch,
  GitHead,
  GitMergeState,
  BranchName,
  RemoteName,
  CommitHash,
} from '../../code-editor-types.js';

import { ok, err, asBranchName, asCommitHash, asRemoteName, now } from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 BRANCH CREATION & DELETION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a new branch in the repository state.
 * Optionally specify the starting commit; defaults to the current HEAD commit.
 * Returns an error if the branch name is invalid or already exists.
 */
export function createBranch(
  state: GitRepositoryState,
  name: string,
  from?: CommitHash
): EditorResult<GitRepositoryState> {
  if (!isValidBranchName(name)) {
    return err('INVALID_BRANCH_NAME', `"${name}" is not a valid branch name`);
  }
  const alreadyExists = state.branches.some((b) => (b.name as string) === name);
  if (alreadyExists) {
    return err('BRANCH_EXISTS', `Branch "${name}" already exists`);
  }
  const commitHash = from ?? state.head.commitHash;
  const branch: GitBranch = {
    name: asBranchName(name),
    commitHash,
    isHead: false,
    ahead: 0,
    behind: 0,
  };
  const updated: GitRepositoryState = {
    ...state,
    branches: [...state.branches, branch],
  };
  return ok(updated, 'BRANCH_CREATED', `Created branch "${name}" at ${(commitHash as string).slice(0, 7)}`);
}

/**
 * Delete a branch from the repository state.
 * Returns an error if the branch is currently checked out or does not exist.
 * Pass `force: true` to skip the checked-out guard.
 */
export function deleteBranch(
  state: GitRepositoryState,
  name: string,
  force: boolean = false
): EditorResult<GitRepositoryState> {
  const branch = state.branches.find((b) => (b.name as string) === name);
  if (!branch) {
    return err('BRANCH_NOT_FOUND', `Branch "${name}" does not exist`);
  }
  if (branch.isHead && !force) {
    return err('CANNOT_DELETE_HEAD', `Cannot delete the currently checked-out branch "${name}"`);
  }
  const branches = state.branches.filter((b) => (b.name as string) !== name);
  return ok({ ...state, branches }, 'BRANCH_DELETED', `Deleted branch "${name}"`);
}

/**
 * Rename a branch in the repository state.
 * Returns an error if the old name doesn't exist or the new name is taken.
 */
export function renameBranch(
  state: GitRepositoryState,
  oldName: string,
  newName: string
): EditorResult<GitRepositoryState> {
  if (!isValidBranchName(newName)) {
    return err('INVALID_BRANCH_NAME', `"${newName}" is not a valid branch name`);
  }
  const idx = state.branches.findIndex((b) => (b.name as string) === oldName);
  if (idx === -1) {
    return err('BRANCH_NOT_FOUND', `Branch "${oldName}" does not exist`);
  }
  const newExists = state.branches.some((b) => (b.name as string) === newName);
  if (newExists) {
    return err('BRANCH_EXISTS', `Branch "${newName}" already exists`);
  }
  const renamed: GitBranch = { ...state.branches[idx]!, name: asBranchName(newName) };
  const branches = [
    ...state.branches.slice(0, idx),
    renamed,
    ...state.branches.slice(idx + 1),
  ];
  // Update HEAD if renaming the current branch
  let head = state.head;
  if (head.type === 'branch' && (head.name as string) === oldName) {
    head = { ...head, name: asBranchName(newName) };
  }
  return ok({ ...state, branches, head }, 'BRANCH_RENAMED', `Renamed branch "${oldName}" to "${newName}"`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 CHECKOUT — switch the working HEAD to a branch
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Checkout a branch, updating HEAD and marking the branch as current.
 * Returns an error if the branch does not exist.
 */
export function checkoutBranch(
  state: GitRepositoryState,
  name: string
): EditorResult<GitRepositoryState> {
  const branch = state.branches.find((b) => (b.name as string) === name);
  if (!branch) {
    return err('BRANCH_NOT_FOUND', `Branch "${name}" does not exist`);
  }
  const branches = state.branches.map((b) => ({
    ...b,
    isHead: (b.name as string) === name,
  }));
  const head: GitHead = {
    type: 'branch',
    name: asBranchName(name),
    commitHash: branch.commitHash,
  };
  return ok({ ...state, branches, head }, 'BRANCH_CHECKED_OUT', `Switched to branch "${name}"`);
}

/**
 * Checkout a detached HEAD at a specific commit hash.
 */
export function checkoutDetached(
  state: GitRepositoryState,
  commitHash: CommitHash
): EditorResult<GitRepositoryState> {
  const head: GitHead = { type: 'detached', commitHash };
  const branches = state.branches.map((b) => ({ ...b, isHead: false }));
  return ok(
    { ...state, branches, head },
    'DETACHED_HEAD',
    `Detached HEAD at ${(commitHash as string).slice(0, 7)}`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 LISTING & QUERYING — filter and inspect branches
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * List branches optionally filtered by scope.
 * `local` returns branches with no upstream. `remote` returns those with upstream.
 * `all` (default) returns every branch.
 */
export function listBranches(
  state: GitRepositoryState,
  filter?: 'local' | 'remote' | 'all'
): GitBranch[] {
  if (!filter || filter === 'all') return [...state.branches];
  if (filter === 'local') return state.branches.filter((b) => !b.upstream);
  if (filter === 'remote') return state.branches.filter((b) => !b.upstream);
  return [...state.branches];
}

/**
 * Get the currently checked-out branch.
 * Returns undefined when HEAD is detached.
 */
export function getCurrentBranch(state: GitRepositoryState): GitBranch | undefined {
  return state.branches.find((b) => b.isHead);
}

/**
 * Find a branch by name. Returns undefined if not found.
 */
export function findBranch(state: GitRepositoryState, name: string): GitBranch | undefined {
  return state.branches.find((b) => (b.name as string) === name);
}

/**
 * Get the ahead/behind divergence counts for a branch relative to its upstream.
 */
export function getBranchDivergence(
  state: GitRepositoryState,
  branchName: string
): EditorResult<{ ahead: number; behind: number }> {
  const branch = findBranch(state, branchName);
  if (!branch) {
    return err('BRANCH_NOT_FOUND', `Branch "${branchName}" does not exist`);
  }
  return ok(
    { ahead: branch.ahead, behind: branch.behind },
    'DIVERGENCE_OK',
    `Branch "${branchName}" is ${branch.ahead} ahead, ${branch.behind} behind`
  );
}

/**
 * Check whether a branch has an upstream tracking branch configured.
 */
export function hasUpstream(branch: GitBranch): boolean {
  return branch.upstream !== undefined;
}

/**
 * Get all branches that are not up to date with their upstream.
 */
export function getStaleLocalBranches(state: GitRepositoryState): GitBranch[] {
  return state.branches.filter((b) => b.behind > 0);
}

/**
 * Get all branches that have un-pushed commits.
 */
export function getAheadBranches(state: GitRepositoryState): GitBranch[] {
  return state.branches.filter((b) => b.ahead > 0);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 UPSTREAM TRACKING — set upstream remote tracking
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Set the upstream tracking branch for a local branch.
 * Returns an error if either the local or remote branch doesn't exist in state.
 */
export function setUpstreamTracking(
  state: GitRepositoryState,
  localBranch: string,
  remoteBranch: string,
  remoteName: string = 'origin'
): EditorResult<GitRepositoryState> {
  const idx = state.branches.findIndex((b) => (b.name as string) === localBranch);
  if (idx === -1) {
    return err('BRANCH_NOT_FOUND', `Local branch "${localBranch}" does not exist`);
  }
  const updated: GitBranch = {
    ...state.branches[idx]!,
    upstream: {
      remote: asRemoteName(remoteName),
      branch: asBranchName(remoteBranch),
    },
  };
  const branches = [
    ...state.branches.slice(0, idx),
    updated,
    ...state.branches.slice(idx + 1),
  ];
  return ok(
    { ...state, branches },
    'UPSTREAM_SET',
    `Set upstream for "${localBranch}" to "${remoteName}/${remoteBranch}"`
  );
}

/**
 * Remove the upstream tracking configuration from a local branch.
 */
export function unsetUpstreamTracking(
  state: GitRepositoryState,
  localBranch: string
): EditorResult<GitRepositoryState> {
  const idx = state.branches.findIndex((b) => (b.name as string) === localBranch);
  if (idx === -1) {
    return err('BRANCH_NOT_FOUND', `Branch "${localBranch}" does not exist`);
  }
  const { upstream: _removed, ...rest } = state.branches[idx]!;
  const updated: GitBranch = rest as GitBranch;
  const branches = [
    ...state.branches.slice(0, idx),
    updated,
    ...state.branches.slice(idx + 1),
  ];
  return ok({ ...state, branches }, 'UPSTREAM_UNSET', `Removed upstream for "${localBranch}"`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 MERGE, REBASE, CHERRY-PICK — high-level operation stubs
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Initiate a merge of the source branch into the current HEAD branch.
 * Updates the merge state to 'merging'. Actual conflict detection requires
 * IO-layer diff computation; this records intent in engine state.
 */
export function mergeBranch(
  state: GitRepositoryState,
  sourceBranch: string,
  strategy: 'recursive' | 'ours' | 'theirs' | 'octopus' = 'recursive'
): EditorResult<GitRepositoryState> {
  const source = findBranch(state, sourceBranch);
  if (!source) {
    return err('BRANCH_NOT_FOUND', `Source branch "${sourceBranch}" does not exist`);
  }
  const current = getCurrentBranch(state);
  if (!current) {
    return err('DETACHED_HEAD', 'Cannot merge in detached HEAD state');
  }
  if ((source.name as string) === (current.name as string)) {
    return err('SAME_BRANCH', 'Cannot merge a branch into itself');
  }
  // In the engine layer we record the merge attempt; conflicts are resolved via git-merge.ts
  return ok(state, 'MERGE_INITIATED', `Merging "${sourceBranch}" into "${current.name}" using ${strategy}`);
}

/**
 * Initiate a rebase of the current branch onto the target branch.
 * Records the intended operation; IO-layer handles actual rebasing.
 */
export function rebaseBranch(
  state: GitRepositoryState,
  ontoBranch: string
): EditorResult<GitRepositoryState> {
  const onto = findBranch(state, ontoBranch);
  if (!onto) {
    return err('BRANCH_NOT_FOUND', `Target branch "${ontoBranch}" does not exist`);
  }
  const current = getCurrentBranch(state);
  if (!current) {
    return err('DETACHED_HEAD', 'Cannot rebase in detached HEAD state');
  }
  if ((current.name as string) === (onto.name as string)) {
    return err('SAME_BRANCH', 'Cannot rebase a branch onto itself');
  }
  return ok(state, 'REBASE_INITIATED', `Rebasing "${current.name}" onto "${ontoBranch}"`);
}

/**
 * Cherry-pick a commit hash into the current branch.
 * Validates that the commit hash format is plausible.
 */
export function cherryPick(
  state: GitRepositoryState,
  commitHash: string
): EditorResult<GitRepositoryState> {
  if (!isValidCommitHash(commitHash)) {
    return err('INVALID_HASH', `"${commitHash}" is not a valid commit hash`);
  }
  const current = getCurrentBranch(state);
  if (!current) {
    return err('DETACHED_HEAD', 'Cannot cherry-pick in detached HEAD state');
  }
  return ok(
    state,
    'CHERRY_PICK_INITIATED',
    `Cherry-picking ${commitHash.slice(0, 7)} onto "${current.name}"`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 VALIDATION HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Validate a proposed branch name against git's naming rules.
 * Rejects empty strings, refs with spaces, consecutive dots, leading dots,
 * trailing slashes, and other invalid patterns.
 */
export function isValidBranchName(name: string): boolean {
  if (!name || name.length === 0) return false;
  if (name.startsWith('-')) return false;
  if (name.startsWith('.')) return false;
  if (name.endsWith('/')) return false;
  if (name.endsWith('.lock')) return false;
  if (/[\s~^:?*\[\\]/.test(name)) return false;
  if (/\.\./.test(name)) return false;
  if (/@\{/.test(name)) return false;
  if (name === '@') return false;
  return true;
}

/**
 * Validate a commit hash string (4-40 hex chars).
 */
function isValidCommitHash(hash: string): boolean {
  return /^[0-9a-f]{4,40}$/i.test(hash);
}

/**
 * Get a display-friendly label for a branch, including its upstream if set.
 */
export function branchLabel(branch: GitBranch): string {
  if (branch.upstream) {
    return `${branch.name as string} → ${branch.upstream.remote as string}/${branch.upstream.branch as string}`;
  }
  return branch.name as string;
}

/**
 * Summarize branch statistics for the repository.
 */
export function branchSummary(state: GitRepositoryState): {
  total: number;
  local: number;
  withUpstream: number;
  ahead: number;
  behind: number;
  current: string | undefined;
} {
  const withUpstream = state.branches.filter((b) => !b.upstream).length;
  const ahead = state.branches.filter((b) => b.ahead > 0).length;
  const behind = state.branches.filter((b) => b.behind > 0).length;
  const current = getCurrentBranch(state);
  return {
    total: state.branches.length,
    local: state.branches.filter((b) => !b.upstream).length,
    withUpstream,
    ahead,
    behind,
    current: current ? (current.name as string) : undefined,
  };
}
